import { WEEKDAY_SLOT_DEFINITIONS } from '@/constants/weekdaySlots';
import type { WeekdayCode } from '@/types';
import { parseCommaSeparatedValues } from '@/utils/stringLists';

export type SlotsByDay = Record<WeekdayCode, string[]>;
export type OldSlotsByDay = Record<WeekdayCode, string[] | null>;
export type SlotTextInputs = Record<WeekdayCode, string>;

export interface ReservationAreaSlotsConfig {
  slotsByDay: SlotsByDay;
  oldSlotsByDay: OldSlotsByDay;
  applySlots: string | null;
}

const areSlotListsEqual = (a: string[], b: string[]): boolean => {
  if (a.length !== b.length) {
    return false;
  }
  return a.every((value, index) => value === b[index]);
};

const shouldUseOldSlots = (applySlots: string | null, referenceDate: Date): boolean => {
  if (!applySlots) {
    return false;
  }

  const applyDate = new Date(`${applySlots}T00:00:00`);
  if (Number.isNaN(applyDate.getTime())) {
    return false;
  }

  const current = new Date(
    referenceDate.getFullYear(),
    referenceDate.getMonth(),
    referenceDate.getDate(),
  );

  return current.getTime() <= applyDate.getTime();
};

export const getActiveSlotsForDay = (
  source: Pick<ReservationAreaSlotsConfig, 'slotsByDay' | 'oldSlotsByDay' | 'applySlots'>,
  day: WeekdayCode,
  referenceDate: Date = new Date(),
): string[] => {
  const currentSlots = source.slotsByDay?.[day] ?? [];
  if (!shouldUseOldSlots(source.applySlots, referenceDate)) {
    return currentSlots;
  }

  const oldSlots = source.oldSlotsByDay?.[day];
  return Array.isArray(oldSlots) && oldSlots.length > 0 ? [...oldSlots] : currentSlots;
};

export const getUpcomingSlotsForDay = (
  source: Pick<ReservationAreaSlotsConfig, 'slotsByDay' | 'oldSlotsByDay' | 'applySlots'>,
  day: WeekdayCode,
  referenceDate: Date = new Date(),
): string[] | null => {
  if (!shouldUseOldSlots(source.applySlots, referenceDate)) {
    return null;
  }

  return source.slotsByDay?.[day] ?? [];
};

export const hasScheduledSlotChange = (
  source: Pick<ReservationAreaSlotsConfig, 'slotsByDay' | 'oldSlotsByDay' | 'applySlots'>,
  day: WeekdayCode,
  referenceDate: Date = new Date(),
): boolean => {
  const upcomingSlots = getUpcomingSlotsForDay(source, day, referenceDate);
  if (!upcomingSlots) {
    return false;
  }

  const activeSlots = getActiveSlotsForDay(source, day, referenceDate);
  return !areSlotListsEqual(activeSlots, upcomingSlots);
};

export const createEmptySlotsByDay = (): SlotsByDay =>
  WEEKDAY_SLOT_DEFINITIONS.reduce(
    (acc, def) => {
      acc[def.code] = [];
      return acc;
    },
    {} as SlotsByDay,
  );

export const createEmptyOldSlotsMap = (): OldSlotsByDay =>
  WEEKDAY_SLOT_DEFINITIONS.reduce(
    (acc, def) => {
      acc[def.code] = null;
      return acc;
    },
    {} as OldSlotsByDay,
  );

export const cloneSlotsByDay = (source?: Partial<Record<WeekdayCode, string[]>> | null): SlotsByDay =>
  WEEKDAY_SLOT_DEFINITIONS.reduce(
    (acc, def) => {
      const current = source?.[def.code];
      acc[def.code] = Array.isArray(current) ? [...current] : [];
      return acc;
    },
    {} as SlotsByDay,
  );

export const cloneOldSlotsByDay = (
  source?: Partial<Record<WeekdayCode, string[] | null>> | null,
): OldSlotsByDay =>
  WEEKDAY_SLOT_DEFINITIONS.reduce(
    (acc, def) => {
      const current = source?.[def.code];
      acc[def.code] = current && current.length > 0 ? [...current] : null;
      return acc;
    },
    {} as OldSlotsByDay,
  );

export const slotsToTextInputs = (source: Partial<Record<WeekdayCode, string[]>> | null = null): SlotTextInputs =>
  WEEKDAY_SLOT_DEFINITIONS.reduce(
    (acc, def) => {
      const values = source?.[def.code] ?? [];
      acc[def.code] = values.length > 0 ? values.join(', ') : '';
      return acc;
    },
    {} as SlotTextInputs,
  );

export const createEmptySlotTextInputs = (): SlotTextInputs => slotsToTextInputs(createEmptySlotsByDay());

export const textInputsToSlots = (inputs: Record<WeekdayCode, string>): SlotsByDay =>
  WEEKDAY_SLOT_DEFINITIONS.reduce(
    (acc, def) => {
      acc[def.code] = parseCommaSeparatedValues(inputs[def.code] ?? '');
      return acc;
    },
    createEmptySlotsByDay(),
  );

export const mapSlotsConfigToSnapshot = (config?: ReservationAreaSlotsConfig | null) => ({
  slotsByDay: cloneSlotsByDay(config?.slotsByDay),
  oldSlotsByDay: cloneOldSlotsByDay(config?.oldSlotsByDay),
  slotsApplyDate: config?.applySlots ?? null,
});
